import { useAnimations } from '@react-three/drei';
import { useEffect, useMemo } from 'react';
import { useGraph } from '@react-three/fiber';
import { SkeletonUtils } from 'three-stdlib';

import { useCompressedGLTF } from 'hooks/hooks';

const url = new URL('static/models/adler_boy_anime4.min.glb', import.meta.url);

export default function Character({
  customOptions,
  gender,
  isMoved,
}: {
  customOptions: {
    type: string;
    hair: number;
    face: number;
    top: number;
    bottom: number;
    shoes: number;
  };
  gender: string;
  isMoved: number;
}) {
  const { scene, animations } = useCompressedGLTF(url.href);
  const clonedScene = useMemo(() => SkeletonUtils.clone(scene), [scene]);
  const { nodes, materials } = useGraph(clonedScene);
  const { ref, actions, names } = useAnimations(animations);

  const matNames: any[] = [];
  const disposeMesh: THREE.Mesh[] = [];
  const disposeTexture: THREE.Texture[] = [];
  Object.values(materials).map((key) => {
    matNames.push(key.name);
  });

  for (let i = 0; i < matNames.length; i++) {
    materials[matNames[i]].toneMapped = false;
  }

  clonedScene.traverse((object) => {
    if ((object as THREE.Mesh).isMesh) {
      const mesh = object as THREE.Mesh;
      disposeMesh.push(mesh);
      const mat = mesh.material as THREE.Material;
      if (mat.map !== null) {
        disposeTexture.push(mat.map);
      }
    }
  });

  useEffect(() => {
    return () => {
      for (let i = 0; i < disposeMesh.length; i++) {
        if (disposeMesh[i]) disposeMesh[i].geometry.dispose();
      }
      for (let i = 0; i < disposeTexture.length; i++) {
        if (disposeTexture[i]) disposeTexture[i].dispose();
      }
    };
  }, []);

  useEffect(() => {
    if (isMoved === 1) {
      actions[names[1]]?.reset().fadeIn(0.5).play();

      return () => {
        actions[names[1]]?.fadeOut(0.5);
      };
    } else {
      actions[names[0]]?.reset().fadeIn(0.5).play();

      return () => {
        actions[names[0]]?.fadeOut(0.5);
      };
    }
  }, [actions, names, isMoved]);
  return (
    <group
      ref={ref}
      castShadow
      rotation={[Math.PI / 2, 0, Math.PI]}
      scale={0.018}
      position={[0, -0.5, 0]}>
      <group name='Scene'>
        <group name='Armature' rotation={[0, 0, 0]} scale={1}>
          <primitive object={nodes.mixamorigHips} />
          {customOptions.type !== 'D' && (
            <group name='boy_body'>
              <skinnedMesh
                castShadow={true}
                frustumCulled={false}
                name='mesh_0'
                geometry={nodes.mesh_0.geometry}
                material={materials['lambert1.004']}
                skeleton={nodes.mesh_0.skeleton}
                position={[-0.5393, 0.0003, -0.146]}
                scale={0.0001}
              />
            </group>
          )}
          {customOptions.type !== 'D' && (
            <group name='boy_face'>
              <skinnedMesh
                castShadow={true}
                frustumCulled={false}
                name='mesh_1'
                geometry={nodes.mesh_1.geometry}
                material={materials['Material.003']}
                skeleton={nodes.mesh_1.skeleton}
                position={[-0.5393, 0.0003, -0.146]}
                scale={0.0001}
              />
            </group>
          )}
          {customOptions.hair == 1 && (
            <group name='boy_hair001'>
              <skinnedMesh
                castShadow={true}
                frustumCulled={false}
                name='mesh_2'
                geometry={nodes.mesh_2.geometry}
                material={materials['Material.010']}
                skeleton={nodes.mesh_2.skeleton}
                position={[-0.5393, 0.0003, -0.146]}
                scale={0.0001}
              />
            </group>
          )}
          {customOptions.hair == 2 && (
            <group name='boy_hair002'>
              <skinnedMesh
                castShadow={true}
                frustumCulled={false}
                name='mesh_3'
                geometry={nodes.mesh_3.geometry}
                material={materials['Material.031']}
                skeleton={nodes.mesh_3.skeleton}
                position={[-0.5393, 0.0003, -0.146]}
                scale={0.0001}
              />
            </group>
          )}
          {customOptions.hair > 2 && (
            <group name='boy_hair002'>
              <skinnedMesh
                castShadow={true}
                frustumCulled={false}
                name='mesh_3'
                geometry={nodes.mesh_3.geometry}
                material={materials['Material.031']}
                skeleton={nodes.mesh_3.skeleton}
                position={[-0.5393, 0.0003, -0.146]}
                scale={0.0001}
              />
            </group>
          )}
          {customOptions.bottom == 1 && (
            <group name='boy_pants001'>
              <group position={[-0.5393, 0.0003, -0.146]} scale={0.0001}>
                <skinnedMesh
                  castShadow={true}
                  frustumCulled={false}
                  name='mesh_4'
                  geometry={nodes.mesh_4.geometry}
                  material={materials['Material.027']}
                  skeleton={nodes.mesh_4.skeleton}
                />
                <skinnedMesh
                  castShadow={true}
                  frustumCulled={false}
                  name='mesh_4_1'
                  geometry={nodes.mesh_4_1.geometry}
                  material={materials['Material.028']}
                  skeleton={nodes.mesh_4_1.skeleton}
                />
              </group>
            </group>
          )}
          {customOptions.bottom == 2 && (
            <group name='boy_pants002'>
              <group position={[-0.5393, 0.0003, -0.146]} scale={0.0001}>
                <skinnedMesh
                  castShadow={true}
                  frustumCulled={false}
                  name='mesh_5'
                  geometry={nodes.mesh_5.geometry}
                  material={materials['Material.002']}
                  skeleton={nodes.mesh_5.skeleton}
                />
                <skinnedMesh
                  castShadow={true}
                  frustumCulled={false}
                  name='mesh_5_1'
                  geometry={nodes.mesh_5_1.geometry}
                  material={materials['Material.001']}
                  skeleton={nodes.mesh_5_1.skeleton}
                />
              </group>
            </group>
          )}
          {customOptions.bottom > 2 && (
            <group name='boy_pants002'>
              <group position={[-0.5393, 0.0003, -0.146]} scale={0.0001}>
                <skinnedMesh
                  castShadow={true}
                  frustumCulled={false}
                  name='mesh_5'
                  geometry={nodes.mesh_5.geometry}
                  material={materials['Material.002']}
                  skeleton={nodes.mesh_5.skeleton}
                />
                <skinnedMesh
                  castShadow={true}
                  frustumCulled={false}
                  name='mesh_5_1'
                  geometry={nodes.mesh_5_1.geometry}
                  material={materials['Material.001']}
                  skeleton={nodes.mesh_5_1.skeleton}
                />
              </group>
            </group>
          )}
          {customOptions.top == 1 && (
            <group name='boy_shirts001'>
              <group position={[-0.5393, 0.0003, -0.146]} scale={0.0001}>
                <skinnedMesh
                  castShadow={true}
                  frustumCulled={false}
                  name='mesh_6'
                  geometry={nodes.mesh_6.geometry}
                  material={materials['Material.022']}
                  skeleton={nodes.mesh_6.skeleton}
                />
                <skinnedMesh
                  castShadow={true}
                  frustumCulled={false}
                  name='mesh_6_1'
                  geometry={nodes.mesh_6_1.geometry}
                  material={materials['Material.023']}
                  skeleton={nodes.mesh_6_1.skeleton}
                />
              </group>
            </group>
          )}
          {customOptions.top == 2 && (
            <group name='boy_shirts002'>
              <group position={[-0.5393, 0.0003, -0.146]} scale={0.0001}>
                <skinnedMesh
                  castShadow={true}
                  frustumCulled={false}
                  name='mesh_7'
                  geometry={nodes.mesh_7.geometry}
                  material={materials['Material.005']}
                  skeleton={nodes.mesh_7.skeleton}
                />
                <skinnedMesh
                  castShadow={true}
                  frustumCulled={false}
                  name='mesh_7_1'
                  geometry={nodes.mesh_7_1.geometry}
                  material={materials['Material.011']}
                  skeleton={nodes.mesh_7_1.skeleton}
                />
              </group>
            </group>
          )}
          {customOptions.top > 2 && (
            <group name='boy_shirts002'>
              <group position={[-0.5393, 0.0003, -0.146]} scale={0.0001}>
                <skinnedMesh
                  castShadow={true}
                  frustumCulled={false}
                  name='mesh_7'
                  geometry={nodes.mesh_7.geometry}
                  material={materials['Material.005']}
                  skeleton={nodes.mesh_7.skeleton}
                />
                <skinnedMesh
                  castShadow={true}
                  frustumCulled={false}
                  name='mesh_7_1'
                  geometry={nodes.mesh_7_1.geometry}
                  material={materials['Material.011']}
                  skeleton={nodes.mesh_7_1.skeleton}
                />
              </group>
            </group>
          )}
          {customOptions.shoes == 1 && (
            <group name='boy_shoes001'>
              <skinnedMesh
                castShadow={true}
                frustumCulled={false}
                name='mesh_8'
                geometry={nodes.mesh_8.geometry}
                material={materials['Material.033']}
                skeleton={nodes.mesh_8.skeleton}
                position={[-0.5393, 0.0003, -0.146]}
                scale={0.0001}
              />
            </group>
          )}
          {customOptions.shoes == 2 && (
            <group name='boy_shoes002'>
              <group position={[-0.5393, 0.0003, -0.146]} scale={0.0001}>
                <skinnedMesh
                  castShadow={true}
                  frustumCulled={false}
                  name='mesh_9'
                  geometry={nodes.mesh_9.geometry}
                  material={materials['Material.015']}
                  skeleton={nodes.mesh_9.skeleton}
                />
                <skinnedMesh
                  castShadow={true}
                  frustumCulled={false}
                  name='mesh_9_1'
                  geometry={nodes.mesh_9_1.geometry}
                  material={materials['Material.014']}
                  skeleton={nodes.mesh_9_1.skeleton}
                />
                <skinnedMesh
                  castShadow={true}
                  frustumCulled={false}
                  name='mesh_9_2'
                  geometry={nodes.mesh_9_2.geometry}
                  material={materials['Material.012']}
                  skeleton={nodes.mesh_9_2.skeleton}
                />
                <skinnedMesh
                  castShadow={true}
                  frustumCulled={false}
                  name='mesh_9_3'
                  geometry={nodes.mesh_9_3.geometry}
                  material={materials['Material.013']}
                  skeleton={nodes.mesh_9_3.skeleton}
                />
              </group>
            </group>
          )}
          {customOptions.shoes > 2 && (
            <group name='boy_shoes002'>
              <group position={[-0.5393, 0.0003, -0.146]} scale={0.0001}>
                <skinnedMesh
                  castShadow={true}
                  frustumCulled={false}
                  name='mesh_9'
                  geometry={nodes.mesh_9.geometry}
                  material={materials['Material.015']}
                  skeleton={nodes.mesh_9.skeleton}
                />
                <skinnedMesh
                  castShadow={true}
                  frustumCulled={false}
                  name='mesh_9_1'
                  geometry={nodes.mesh_9_1.geometry}
                  material={materials['Material.014']}
                  skeleton={nodes.mesh_9_1.skeleton}
                />
                <skinnedMesh
                  castShadow={true}
                  frustumCulled={false}
                  name='mesh_9_2'
                  geometry={nodes.mesh_9_2.geometry}
                  material={materials['Material.012']}
                  skeleton={nodes.mesh_9_2.skeleton}
                />
                <skinnedMesh
                  castShadow={true}
                  frustumCulled={false}
                  name='mesh_9_3'
                  geometry={nodes.mesh_9_3.geometry}
                  material={materials['Material.013']}
                  skeleton={nodes.mesh_9_3.skeleton}
                />
              </group>
            </group>
          )}
        </group>
      </group>
    </group>
  );
}
